// frontend/src/pages/Login.jsx
import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import api from '../api';

export default function Login() {
  const [activeTab, setActiveTab] = useState('password');
  const [form, setForm] = useState({ phone_number: '', password: '' });
  const [code, setCode] = useState(['', '', '', '', '']);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);
  const navigate = useNavigate();
  const codeInputRef = useRef([]);
  
  // تایمر
  useEffect(() => {
    let interval;
    if (isCodeSent && timer > 0 && activeTab === 'code') {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    } else if (timer <= 0) {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [isCodeSent, timer, activeTab]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if ('OTPCredential' in window && isCodeSent && activeTab === 'code') {
      const ac = new AbortController();
  
      navigator.credentials
        .get({
          otp: { transport: ['sms'] },
          signal: ac.signal,
        })
        .then((otp) => {
          if (otp && otp.code) {
            const receivedCode = otp.code;
            if (receivedCode.length === 5) {
              const digits = receivedCode.split('');
              setCode(digits);
              
              // فوکوس روی آخرین اینپوت
              setTimeout(() => {
                codeInputRef.current[4]?.focus();
              }, 100);
  
              // خودکار تأیید کن (اختیاری)
              setTimeout(() => {
                handleVerifyCode(new Event('submit'));
              }, 500);
            }
          }
        })
        .catch((err) => {
          console.log('OTP auto-fill failed or cancelled', err);
        });
  
      return () => ac.abort();
    }
  }, [isCodeSent, activeTab]);

  
  const handleCodeChange = (e, index) => {
    const value = e.target.value.slice(0, 1);
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 4) {
      codeInputRef.current[index + 1]?.focus();
    } else if (!value && index > 0) {
      codeInputRef.current[index - 1]?.focus();
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const res = await api.post('/api/auth/login', form);
      const { accessToken, refreshToken, user } = res.data;

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));

      setSuccess(`خوش آمدید ${user.name || user.first_name}`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'شماره تلفن یا رمز عبور اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleRequestCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      await api.post('/api/auth/login/request-code', {
        phone_number: form.phone_number,
      });
      setIsCodeSent(true);
      setTimer(60);
      setCanResend(false);
      setCode(['', '', '', '', '']);
      setSuccess('کد تأیید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال کد');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleResendCode = async () => {
    setError('');
    setSuccess('');
    try {
      await api.post('/api/auth/login/request-code', {
        phone_number: form.phone_number,
      });
      setCode(['', '', '', '', '']);
      setTimer(60);
      setCanResend(false);
      setSuccess('کد جدید ارسال شد');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'خطا در ارسال مجدد');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const verificationCode = code.join('');
    if (verificationCode.length !== 5) {
      setError('کد ۵ رقمی را کامل وارد کنید');
      setTimeout(() => setError(''), 3000);
      return;
    }

    try {
      const res = await api.post('/api/auth/verify', {
        phone_number: form.phone_number,
        code: verificationCode,
      });
      const { accessToken, refreshToken, user } = res.data;

      localStorage.setItem('authToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('userInfo', JSON.stringify(user));

      setSuccess(`خوش آمدید ${user.name || user.first_name}`);
      setTimeout(() => navigate('/'), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'کد اشتباه است');
      setTimeout(() => setError(''), 3000);
    }
  };

  // --- پیام‌ها ---
  const Toast = ({ type, message }) => (
    <motion.div
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      exit={{ y: -100, opacity: 0 }}
      transition={{ type: "spring", stiffness: 300 }}
      className={`fixed top-4 left-1/2 transform -translate-x-1/2 z-50 px-6 py-4 rounded-2xl shadow-2xl border flex items-center gap-3 ${
        type === 'error' ? 'bg-red-500/90 text-white border-red-300' : 'bg-green-500/90 text-white border-green-300'
      } backdrop-blur-lg`}
    >
      <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        {type === 'error' ? (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" />
        ) : (
          <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
        )}
      </svg>
      <span className="font-medium">{message}</span>
    </motion.div>
  );

  return (
    <>
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center px-4 py-8 font-vazir">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ type: "spring", stiffness: 100 }}
          className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-2xl p-8 w-full max-w-md border border-white/30"
        >

          {/* عنوان */}
          <motion.h2
            initial={{ y: -20 }}
            animate={{ y: 0 }}
            className="text-3xl font-bold text-center bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent mb-8"
          >
            ورود به حساب
          </motion.h2>

          {/* تب‌ها */}
          <div className="flex justify-center mb-8">
            <div className="bg-gray-100 p-1 rounded-2xl flex gap-1">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab('password')}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'password'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600'
                }`}
              >
                رمز عبور
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setActiveTab('code')}
                className={`px-6 py-3 rounded-xl font-medium transition-all ${
                  activeTab === 'code'
                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-lg'
                    : 'text-gray-600'
                }`}
              >
                کد تأیید
              </motion.button>
            </div>
          </div>

          {/* فرم‌ها */}
          <AnimatePresence mode="wait">
            {activeTab === 'password' ? (
              <motion.form
                key="password"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                onSubmit={handlePasswordLogin}
                className="space-y-5"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                  <motion.input
  whileFocus={{ scale: 1.02 }}
  name="phone_number"
  type="text"
  value={form.phone_number}
  onChange={handleChange}
  required
  placeholder="۰۹۱۲۳۴۵۶۷۸۹"
  disabled={isCodeSent}
  autoComplete="one-time-code"  // این خط مهم است
  inputMode="numeric"
  className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50 disabled:bg-gray-100"
/>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">رمز عبور</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    required
                    placeholder="••••••••"
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                  />
                </div>
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  ورود با رمز عبور
                </motion.button>
              </motion.form>
            ) : (
              <motion.form
                key="code"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                onSubmit={isCodeSent ? handleVerifyCode : handleRequestCode}
                className="space-y-6"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">شماره تلفن</label>
                  <motion.input
                    whileFocus={{ scale: 1.02 }}
                    name="phone_number"
                    type="text"
                    value={form.phone_number}
                    onChange={handleChange}
                    required
                    placeholder="۰۹۱۲۳۴۵۶۷۸۹"
                    disabled={isCodeSent}
                    className="w-full px-4 py-3 border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50 disabled:bg-gray-100"
                  />
                </div>

                {isCodeSent && (
                  <>
                    <p className="text-center text-sm text-gray-600">
                      کد ۵ رقمی به <span className="font-mono text-indigo-700">{form.phone_number}</span> ارسال شد
                    </p>
                    <div className="flex justify-center gap-3" dir="ltr">
                      {code.map((digit, index) => (
                        <motion.input
                          key={index}
                          ref={(el) => (codeInputRef.current[index] = el)}
                          type="text"
                          inputMode="numeric"
                          value={digit}
                          onChange={(e) => handleCodeChange(e, index)}
                          className="w-14 h-14 text-center text-2xl font-bold border-2 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-300 focus:border-indigo-600 transition-all bg-gray-50"
                          maxLength={1}
                          required
                          initial={{ scale: 0.8 }}
                          animate={{ scale: 1 }}
                          transition={{ delay: index * 0.05 }}
                        />
                      ))}
                    </div>

                    <div className="text-center">
                      {!canResend ? (
                        <p className="text-sm text-gray-500">{timer} ثانیه تا ارسال مجدد</p>
                      ) : (
                        <motion.button
                          type="button"
                          whileHover={{ scale: 1.05 }}
                          onClick={handleResendCode}
                          className="text-indigo-600 font-medium underline"
                        >
                          ارسال مجدد کد
                        </motion.button>
                      )}
                    </div>
                  </>
                )}

                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  type="submit"
                  className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 text-white py-4 rounded-2xl font-bold shadow-lg hover:shadow-xl transition-all"
                >
                  {isCodeSent ? 'تأیید کد' : 'ارسال کد تأیید'}
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>

          {/* ثبت‌نام */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="text-center mt-8"
          >
            <p className="text-sm text-gray-600 mb-2">هنوز حساب ندارید؟</p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/register_page')}
              className="bg-gradient-to-r from-purple-500 to-pink-600 text-white px-8 py-3 rounded-full font-bold shadow-lg hover:shadow-xl transition-all"
            >
              ثبت‌نام کنید
            </motion.button>
          </motion.div>
        </motion.div>

        {/* پیام‌ها */}
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 w-full max-w-sm px-4">
          <AnimatePresence>
            {error && <Toast type="error" message={error} />}
            {success && <Toast type="success" message={success} />}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
}